import { useMemo } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { useDiscoveryOptional } from '../contexts/discoveryStore';
import type { UserPreferences, UserContextType } from '../contexts/UserContext';

/**
 * Hook for accessing and managing user preferences.
 *
 * Bridge (T010-FE): in `v2` discovery mode the normalized URL is the sole
 * active authority, so the two URL-derived fields consumers read —
 * `effectiveLocation` and `locationResolutionPending` — come from
 * `DiscoveryProvider` (the resolved territory + the no-nationwide-interim-fetch
 * gate) instead of the legacy session override. In `legacy`/`loading` mode, or
 * when no `DiscoveryProvider` is mounted (organizer host, isolated tests), the
 * raw `UserContext` is returned unchanged, so the pre-activation behaviour and
 * every existing consumer/test are preserved byte-for-byte.
 *
 * S3 (T010 review): a FAILED v2 territorial resolution (`status:'unavailable'`)
 * keeps the gate closed — the feed must never fetch with the stored/saved
 * geography under a failed territorial URL (plan l.82; T016 renders the error +
 * retry UI on top of this hold).
 *
 * Saved intent (`preferences`) and the explicit action methods always come from
 * `UserContext` — a URL visit never writes preferences (R8), and a saved
 * preference is only the initial intent for a generic-entry replacement. In
 * `v2` the record behind them is the versioned store (`preferencesV2`,
 * T011-FE): a single comune UUID plus an independent `radiusKm`.
 *
 * @returns User preferences and methods to update them
 */
export function useUserPreferences(): UserContextType {
  const userCtx = useUserContext();
  const discovery = useDiscoveryOptional();

  return useMemo<UserContextType>(() => {
    if (!discovery) return userCtx;
    // T016-FE: in `upgrade-required` the blocking state owns the session
    // (plan l.71/74). Hold every consumer that respects this gate (feed,
    // galleries) instead of letting them fire requests the activated server
    // would reject 426; the saved location is NOT applied as active geography
    // and the legacy override machinery stays untouched.
    if (discovery.mode === 'upgrade-required') {
      return { ...userCtx, locationResolutionPending: true };
    }
    if (discovery.mode !== 'v2') return userCtx;
    return {
      ...userCtx,
      effectiveLocation: discovery.activeLocation ?? userCtx.preferences.location,
      // Held while the active location is unknown or unusable: a pending
      // resolution, a failed one (`unavailable`), or an invalid URL (`invalid`,
      // W-B) — the feed must never fall back to stored geography in those states.
      locationResolutionPending:
        discovery.pending ||
        discovery.status === 'unavailable' ||
        discovery.status === 'invalid' ||
        // T011-FE: the v1→v2 conversion has not landed yet (`preferencesV2`
        // is null while the gate is open). The feed must neither query with
        // the pre-migration geography nor fall back to a broader/nationwide
        // scope during that window — it is held until the v2 record exists.
        userCtx.preferencesV2 === null,
    };
  }, [userCtx, discovery]);
}

export type { UserPreferences, UserContextType };
